import React from "react";
import { cn } from "@gettactic/helpers";

export type SlidingNodesProps = {
  children: React.ReactNode[];
  hoverDirection?: "left" | "right";
  spacingClass?: string;
  hoverSpacing?: number;
  className?: string;
  duration?: number;
  hoverToFront?: boolean;
};

export const SlidingNodes = ({
  children,
  hoverDirection = "left",
  spacingClass = "-space-x-3",
  hoverSpacing = 2,
  className,
  duration = 300,
  hoverToFront = false
}: SlidingNodesProps) => {
  const validChildren = React.Children.toArray(children).filter(Boolean);
  const totalItems = validChildren.length;

  // Map duration to preset Tailwind classes
  const getDurationClass = (dur: number) => {
    if (dur <= 150) return "duration-150";
    if (dur <= 200) return "duration-200";
    if (dur <= 300) return "duration-300";
    if (dur <= 500) return "duration-500";
    return "duration-700";
  };

  // Map z-index to preset Tailwind classes
  const getZIndexClass = (index: number) => {
    const zValue = totalItems - index;
    if (zValue === 1) return "z-1";
    if (zValue === 2) return "z-2";
    if (zValue === 3) return "z-3";
    if (zValue === 4) return "z-4";
    if (zValue >= 5) return "z-5";
    return "z-0";
  };

  // Map spacing to preset Tailwind classes
  const getTranslateClass = (
    spacing: number,
    direction: string,
    multiplier = 1
  ) => {
    const value = spacing * multiplier;
    if (direction === "left") {
      if (value === 1) return "group-hover:-translate-x-1";
      if (value === 2) return "group-hover:-translate-x-2";
      if (value === 3) return "group-hover:-translate-x-3";
      if (value === 4) return "group-hover:-translate-x-4";
      return "group-hover:-translate-x-2";
    }

    // Right direction
    if (value === 1) return "group-hover:translate-x-1";
    if (value === 2) return "group-hover:translate-x-2";
    if (value === 3) return "group-hover:translate-x-3";
    if (value === 4) return "group-hover:translate-x-4";
    return "group-hover:translate-x-2";
  };

  const durationClass = getDurationClass(duration);

  return (
    <div className={cn("group flex items-center", spacingClass, className)}>
      {validChildren.map((child, index) => {
        const isLastItem = index === totalItems - 1;
        const isFirstItem = index === 0;

        let hoverClassNames = "";
        let zIndexClasses = "";

        // Apply z-index based on position when hoverToFront is enabled
        if (hoverToFront) {
          zIndexClasses = `${getZIndexClass(index)} hover:z-50`;
        }

        if (totalItems > 1) {
          if (hoverDirection === "left") {
            if (!isLastItem) {
              hoverClassNames = `transition-transform ${durationClass}`;

              hoverClassNames += ` ${getTranslateClass(hoverSpacing, "left")}`;

              if (isFirstItem && totalItems > 2) {
                hoverClassNames = `transition-transform ${durationClass} ${getTranslateClass(hoverSpacing, "left", 2)}`;
              }
            }
          } else {
            if (!isFirstItem) {
              hoverClassNames = `transition-transform ${durationClass}`;

              hoverClassNames += ` ${getTranslateClass(hoverSpacing, "right")}`;

              if (isLastItem && totalItems > 2) {
                hoverClassNames = `transition-transform ${durationClass} ${getTranslateClass(hoverSpacing, "right", 2)}`;
              }
            }
          }
        }

        return (
          <div
            key={index}
            className={cn("relative", hoverClassNames, zIndexClasses)}
          >
            {child}
          </div>
        );
      })}
    </div>
  );
};
